import {
	CalendarDateTime,
	getLocalTimeZone,
	parseDate,
	Time,
} from "@internationalized/date";
import { Calendar } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useDateFormatter } from "react-aria";
import type { TimeValue } from "react-aria-components";
import {
	DateInput as AriaDateInput,
	DatePicker as AriaDatePicker,
	DateSegment as AriaDateSegment,
	Dialog as AriaDialog,
	Group as AriaGroup,
	Popover as AriaPopover,
	TimeField as AriaTimeField,
} from "react-aria-components";
import { Calendar as CalendarComponent } from "@/components/application/date-picker/calendar";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/input/label";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

interface ConditionDateValueInputProps {
	label: string;
	value: string;
	onChange: (v: string) => void;
	/** When true, shows a time field in the popover alongside the calendar. */
	showTime?: boolean;
}

function parseTimeFromString(raw: string): TimeValue | null {
	if (!raw.includes("T")) return null;
	const timePart = raw.split("T")[1];
	if (!timePart) return null;
	const [h, m] = timePart.split(":").map(Number);
	if (Number.isNaN(h) || Number.isNaN(m)) return null;
	return new Time(h, m);
}

function formatTime12h(t: TimeValue): string {
	const h12 = t.hour % 12 || 12;
	const min = String(t.minute).padStart(2, "0");
	const ampm = t.hour >= 12 ? "PM" : "AM";
	return `${h12}:${min} ${ampm}`;
}

/**
 * A date (and optionally date+time) picker for workflow condition value inputs.
 * Takes a plain ISO date string ("2024-06-15") or ISO datetime string
 * ("2024-06-15T14:30") and returns one via `onChange`.
 */
export function ConditionDateValueInput({
	label,
	value,
	onChange,
	showTime = false,
}: ConditionDateValueInputProps) {
	const { t } = useAppTranslation();
	const dateFormatter = useDateFormatter({
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	// Track date and time as separate pieces of internal state.
	// They are only emitted via onChange when the user presses Apply.
	const [selectedDate, setSelectedDate] = useState(() =>
		value ? parseDate(value.split("T")[0]) : null,
	);
	const [selectedTime, setSelectedTime] = useState<TimeValue | null>(() =>
		showTime ? parseTimeFromString(value) : null,
	);

	// Sync internal state when the external value prop changes (e.g. when the
	// parent resets value to "" after a field change).
	useEffect(() => {
		setSelectedDate(value ? parseDate(value.split("T")[0]) : null);
		setSelectedTime(showTime ? parseTimeFromString(value) : null);
	}, [value, showTime]);

	const formattedDisplay = selectedDate
		? (() => {
				const datePart = dateFormatter.format(
					selectedDate.toDate(getLocalTimeZone()),
				);
				return showTime && selectedTime
					? `${datePart} ${t("common.datePicker.timeAt")} ${formatTime12h(selectedTime)}`
					: datePart;
			})()
		: t("common.datePicker.selectDate");

	const handleApply = (close: () => void) => {
		if (selectedDate) {
			if (showTime && selectedTime) {
				const dt = new CalendarDateTime(
					selectedDate.year,
					selectedDate.month,
					selectedDate.day,
					selectedTime.hour,
					selectedTime.minute,
				);
				onChange(dt.toString());
			} else {
				onChange(selectedDate.toString());
			}
		}
		close();
	};

	const handleClear = (close: () => void) => {
		setSelectedDate(null);
		setSelectedTime(null);
		onChange("");
		close();
	};

	return (
		<div className="space-y-1.5">
			<Label>{label}</Label>
			<AriaDatePicker
				aria-label={label}
				shouldCloseOnSelect={false}
				value={selectedDate}
				onChange={setSelectedDate}
			>
				<AriaGroup>
					<Button
						size="sm"
						color="secondary"
						iconLeading={Calendar}
						className="w-full justify-start"
					>
						{formattedDisplay}
					</Button>
				</AriaGroup>
				<AriaPopover
					offset={8}
					placement="bottom start"
					className={({ isEntering, isExiting }) =>
						cx(
							"origin-(--trigger-anchor-point) will-change-transform",
							isEntering &&
								"duration-150 ease-out animate-in fade-in placement-bottom:slide-in-from-top-0.5 placement-top:slide-in-from-bottom-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out placement-bottom:slide-out-to-top-0.5 placement-top:slide-out-to-bottom-0.5",
						)
					}
				>
					<AriaDialog className="rounded-2xl bg-primary shadow-xl ring ring-secondary_alt">
						{({ close }) => (
							<>
								<div className="flex px-6 py-5">
									<CalendarComponent />
								</div>

								{showTime && (
									<div className="border-t border-secondary px-6 py-4">
										<p className="mb-2 text-sm font-medium text-secondary">
											{t("common.datePicker.time")}
										</p>
										<AriaTimeField
											value={selectedTime}
											onChange={setSelectedTime}
											aria-label={t("common.datePicker.time")}
											hourCycle={12}
										>
											<AriaDateInput className="flex rounded-lg bg-primary px-2.5 py-2 text-md shadow-xs ring-1 ring-inset ring-primary focus-within:ring-2 focus-within:ring-brand">
												{(segment) => (
													<AriaDateSegment
														segment={segment}
														className={cx(
															"rounded px-0.5 text-primary tabular-nums caret-transparent focus:bg-brand-solid focus:font-medium focus:text-white focus:outline-hidden",
															segment.isPlaceholder &&
																"text-placeholder uppercase",
															segment.type === "literal" &&
																"text-fg-quaternary",
														)}
													/>
												)}
											</AriaDateInput>
										</AriaTimeField>
									</div>
								)}

								<div className="grid grid-cols-2 gap-3 border-t border-secondary p-4">
									<Button
										size="sm"
										color="secondary"
										onClick={() => handleClear(close)}
									>
										{t("common.datePicker.clear")}
									</Button>
									<Button
										size="sm"
										color="primary"
										onClick={() => handleApply(close)}
									>
										{t("common.filters.dateRange.apply")}
									</Button>
								</div>
							</>
						)}
					</AriaDialog>
				</AriaPopover>
			</AriaDatePicker>
		</div>
	);
}
